import { getWebsite, getWebsitePlatform } from '@va/dashboard/selectors/core';
import { getSupportCenterDomain } from '@va/dashboard/selectors/customization';
import { SettingsHeading } from '@va/dashboard/ui/pages';
import { PauseIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { mapPlatform } from '@va/standalone/shared/helpers';
import { contributorRole } from '@va/standalone/shared/selectors';
import { Button, CopyableSection, InputWithSubsection, ToggleActionCard } from '@va/ui/design-system';
import { is3asApp, withProtocol } from '@va/util/helpers';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useIgnoreBrowserTracking, { IGNORE_TRACKING_VALUES } from './useIgnoreBrowserTracking';
import { useIgnoredIPs } from './useIgnoredIPs';

export const IgnoreOwnVisitsCard = () => {
  const translate = useTranslate();
  const supportCenterDomain = useSelector(getSupportCenterDomain);
  const website = useSelector(getWebsite);
  const platform = useSelector(getWebsitePlatform);
  const platformId = mapPlatform(platform);
  const ignoreLink = useMemo(() => {
    if (!website.url || !platformId || !website.hash) return '';
    return `${withProtocol(website.url)}?visa_${platformId}_${website.hash}=`;
  }, [platformId, website.hash, website.url]);

  const {
    onSubmit,
    onRemove,
    onChange,
    submitSuccess,
    isLoadingCurrentIgnoredIPs: isLoading,
    inputValue,
    ignoredIPs,
    error,
    hasMadeChanges,
    onSave,
    isLoadingUpdateCall,
  } = useIgnoredIPs();

  const { onToggle, isIgnoreBrowserTrackingEnabled } = useIgnoreBrowserTracking();

  const isContributor = useSelector(contributorRole);

  return (
    <div className='flex flex-col gap-18px items-start'>
      <SettingsHeading
        title={translate('card.ignoreSelfVisits.title')}
        tooltip={translate('card.ignoreSelfVisits.tooltip', { supportCenterDomain })}
      />
      <div className='flex flex-col gap-4 w-full'>
        <ToggleActionCard
          handleChange={onToggle}
          icon={() => (
            <div className='flex items-center justify-center w-12 h-12 rounded-full bg-white'>
              <PauseIcon color='#3C3C3C' className='w-6 h-6' />
            </div>
          )}
          title={translate('card.ignoreSelfVisits.ignoreText')}
          name='browserTracking'
          subtitle={translate('card.ignoreSelfVisits.info')}
          value={isIgnoreBrowserTrackingEnabled}
        />
        <CopyableSection
          info={translate('card.ignoreSelfVisits.ignoreLinkText')}
          label={translate('card.ignoreSelfVisits.ignoreTrackingLink')}
          value={ignoreLink + IGNORE_TRACKING_VALUES.STOP_TRACKING}
          copyBtnOptions={{ hidden: is3asApp() }}
        />
        <InputWithSubsection
          disabled={isContributor}
          infoTooltip={isContributor ? translate('tooltips.contributorRole') : undefined}
          isLoading={isLoading}
          onSubmit={onSubmit}
          onRemove={onRemove}
          listItems={ignoredIPs ?? []}
          itemsLabel={translate('card.ignoreSelfVisits.items.label')}
          successSubmit={submitSuccess}
          inputProps={{
            value: inputValue,
            onChange: onChange,
            placeholder: translate('card.ignoreSelfVisits.ignoreIpAddressOrRangeInput.placeholder'),
            name: 'ignoredIp',
            label: translate('card.ignoreSelfVisits.ignoreIpAddressOrRangeInput.label'),
          }}
          error={error}
        />
        {hasMadeChanges && (
          <Button disabled={isLoadingUpdateCall} onClick={onSave} text={translate('button.saveChanges')} />
        )}
      </div>
    </div>
  );
};
